import { useEffect, useRef } from 'react';

/**
 * Runs an element's CSS animations only while it is on screen. The animations are declared in
 * info.css and paused by default; this sets `data-play` on the element while at least a fifth of
 * it is visible, so they start when scrolled into view and pause off screen. Paused animations sit
 * on their first frame, which is always the complete, readable state of the illustration (the same
 * thing the pre-rendered HTML and reduced motion show), so starting one never makes it jump.
 * Under reduced motion it does nothing, and index.css switches every animation off anyway.
 */
export function usePlay<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (typeof IntersectionObserver === 'undefined') {
      el.dataset.play = '';
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) el.dataset.play = '';
        else delete el.dataset.play;
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}
